import { AiStoryAudienceType, AiStoryGoalType } from 'Generated/graphql-types'

type Content = {
  header: {
    copy: string
    variant: 'h3' | 'h4'
  }
  body?: {
    copy: JSX.Element
  }
}

export const explanationCopy: Record<string, Content> = {
  initial: {
    header: {
      copy: 'Getting started with AI',
      variant: 'h3',
    },
    body: {
      copy: (
        <>
          <p>
            Never share information with AI that you wouldn’t want publicly
            available.{' '}
          </p>
          <p>
            For the best results, provide detailed responses to each prompt.
          </p>
          <p>
            Always review AI-generated content as it might write incorrect,
            offensive, and biased information.
          </p>
        </>
      ),
    },
  },
  goal: {
    header: {
      copy: 'Goal-Powered Stories',
      variant: 'h4',
    },
    body: {
      copy: (
        <>
          <p>
            If you&apos;ve set Goals, we can use your progress to craft a story,
            you will be able to edit it before publishing.
          </p>
          <p>
            The more info in your goal, the more accurate your AI story will be!
          </p>
        </>
      ),
    },
  },
  purpose: {
    header: {
      copy: 'Select what you hope to accomplish with this story.',
      variant: 'h4',
    },
  },
  audience: {
    header: {
      copy: 'Select the audience this story is meant for.',
      variant: 'h4',
    },
  },
  impact: {
    header: {
      copy: 'Include both qualitative and quantitative impact over a specific timeframe.',
      variant: 'h4',
    },
    body: {
      copy: (
        <>
          <div>
            <p className='example'>Example #1</p>
            From January to February, we increased the amount of people
            attending our after school program from 100 students per month to
            200 students per month. To quote a parent working three jobs whose
            two kids leverage the program, “This after school program has been a
            game changer for me and my kids. I’m so thankful for it!”
          </div>
          <div>
            <p className='example'>Example #2</p>
            In Q1 2024, we accomplished our fundraising goal of $100k for a new
            facility for senior dogs and cats (ages 8+). The facility will
            provide comfortable housing, nutritious food, veterinary care, and
            quality of life care. As Sabrina James, DC Animal Control Director,
            said, “This new facility will be a game changer in improving animal
            welfare in the city.” We’ll be breaking ground on the new facility
            in early April 2024.
          </div>
        </>
      ),
    },
  },
  importance: {
    header: {
      copy: 'Connect your impact to the values of your audience.',
      variant: 'h4',
    },
    body: {
      copy: (
        <>
          <div>
            <p className='example'>Example #1</p>
            This impact is deeply aligned with the purpose of the Bisky
            Foundation’s grant, which is to provide after school programs for
            working parents.
          </div>
          <div>
            <p className='example'>Example #2</p>
            This facility will support the most vulnerable and hardest to adopt
            dogs and cats in the city, elevating city-wide animal welfare.
          </div>
        </>
      ),
    },
  },
  additionalRequests: {
    header: {
      copy: 'Additional requests could include specific instructions on tone or length.',
      variant: 'h4',
    },
    body: {
      copy: (
        <>
          <div>
            <p className='example'>Example #1</p>
            Write this story with a heartfelt tone.
          </div>
          <div>
            <p className='example'>Example #2</p>
            Make this story 3 paragraphs long.
          </div>
        </>
      ),
    },
  },
}

export const purposes = [
  {
    text: 'Provide update',
    value: AiStoryGoalType.ProvideUpdate,
  },
  {
    text: 'Raise funds',
    value: AiStoryGoalType.RaiseFunds,
  },
  {
    text: 'Spread awareness',
    value: AiStoryGoalType.SpreadAwareness,
  },
  {
    text: 'Share gratitude',
    value: AiStoryGoalType.ShareGratitude,
  },
]

export const audiences = [
  {
    text: 'Board members',
    value: AiStoryAudienceType.BoardMembers,
  },
  {
    text: 'Clients',
    value: AiStoryAudienceType.Clients,
  },
  {
    text: 'Donors',
    value: AiStoryAudienceType.Donors,
  },
  {
    text: 'Employees',
    value: AiStoryAudienceType.Employees,
  },
  {
    text: 'General audience',
    value: AiStoryAudienceType.GeneralAudience,
  },
  {
    text: 'Grantmakers',
    value: AiStoryAudienceType.Grantmakers,
  },
  {
    text: 'Volunteers',
    value: AiStoryAudienceType.Volunteers,
  },
]
